import { BUILDING_DEFS, TRADE_PRICES, TRADE_GROUPS, parseTradeAction, type Building, type BuildingType } from "@tribez/shared";

/** Per-open context. StorageOutpost trade rows gate on `trade` (the viewer's gold
 *  + per-resource holdings) + `isEnemy` (lock blocks non-owner buys). Other
 *  buildings ignore the trade fields. */
export interface MenuOpts {
  isOwner: boolean;
  inRange: boolean;
  /** True when the building belongs to another team (StorageOutpost cross-tribe trade). */
  isEnemy?: boolean;
  /** Viewer economy snapshot for trade-row gating. */
  trade?: { gold: number; hold: Record<string, number> };
}

export interface BuildingMenu {
  /** Open the menu for a specific building. Replaces any existing menu. */
  open(b: Building, opts: MenuOpts): void;
  close(): void;
  /** Refresh hp/state/range. Call per frame while open. */
  refresh(b: Building, opts: MenuOpts): void;
  isOpen(): boolean;
  currentId(): string | null;
  onAction(handler: (act: { buildingId: string; action: string }) => void): void;
}

interface MenuAction { id: string; label: string; cost?: string; }

function actionsFor(type: BuildingType): MenuAction[] {
  switch (type) {
    case "Tent":            return [{ id: "salvage", label: "Salvage (50%)" }];
    case "Campfire":        return [
      { id: "openCraft", label: "Cook" },
      { id: "lightCampfire", label: "Light Campfire", cost: "1 Lumber" },
      { id: "refreshCampfire", label: "Refresh", cost: "1 Lumber" },
      { id: "douseFire", label: "Douse Fire" },
      { id: "lightTorch", label: "Light Torch (carry)" },
    ];
    case "Palisade":        return [{ id: "salvage", label: "Salvage (50%)" }];
    case "Forge":           return [
      { id: "openCraft", label: "Open craft menu (E)" },
      { id: "salvage", label: "Salvage (50%)" },
    ];
    case "Workbench":       return [
      { id: "openCraft", label: "Open craft menu (E)" },
      { id: "salvage", label: "Salvage (50%)" },
    ];
    case "HuntersLodge":    return [
      { id: "thickHide",        label: "Thick Hide (placeholder)" },
      { id: "companionship",    label: "Companionship (placeholder)" },
      { id: "loyalty",          label: "Loyalty (placeholder)" },
      { id: "salvage",          label: "Salvage (50%)" },
    ];
    // StorageOutpost has a dedicated tabbed two-button renderer (renderStorage);
    // it does NOT go through actionsFor.
    case "StorageOutpost":  return [];
    case "Tavern":          return [
      { id: "openShop", label: "Spell Shop (E)" },
      { id: "salvage",  label: "Salvage (50%)" },
    ];
    case "Farm":            return [
      { id: "foxBait",          label: "Fox Bait" },
      { id: "domesticatedSheep", label: "Domesticate Sheep" },
      { id: "salvage", label: "Salvage (50%)" },
    ];
    case "AutomatedDefenseTurret": return [{ id: "salvage", label: "Salvage (50%)" }];
    case "SpikeTrap":       return [{ id: "sharpen", label: "Sharpen (refill charges)" }];
    case "Wonder":          return [];   // hover-tooltip-only
    default:                return [];
  }
}

/** Actions visible to this viewer — owner-only rows (salvage, lockStorage) drop
 *  for non-owners. */
function visibleActions(type: BuildingType, isOwner: boolean): MenuAction[] {
  return actionsFor(type).filter((a) => (a.id !== "salvage" && a.id !== "lockStorage") || isOwner);
}

/** Display label for an action given live building state (the lock row flips
 *  Lock ⇄ Unlock with `b.locked`). */
function actLabel(a: MenuAction, b: Building): string {
  if (a.id === "lockStorage") return b.locked ? "Unlock Storage" : "Lock Storage";
  return a.label;
}

/** True if a StorageOutpost trade row is currently un-clickable (grayed):
 *  sell → no units held; buy → can't afford or locked-out non-owner. Non-trade
 *  rows never gray here (range gating is the separate `--oor` class). */
function tradeRowOff(b: Building, opts: MenuOpts, actId: string): boolean {
  const t = parseTradeAction(actId);
  if (!t) return false;
  const price = TRADE_PRICES[t.key];
  if (t.side === "sell") return (opts.trade?.hold[t.key] ?? 0) < 1;
  if (b.locked && !opts.isOwner) return true; // LockStorage blocks non-owner buys
  return (opts.trade?.gold ?? 0) < price.buy;
}

/**
 * DOM caches captured during `open()` so `refresh()` can mutate fields in
 * place. Re-rendering innerHTML every frame breaks Playwright's native
 * `locator.click()` (and presumably racy human clicks): the row element the
 * locator resolves gets detached the next frame, and the click misses. Stable
 * DOM = stable click target.
 */
interface MenuRefs {
  bId: string;
  hpEl: HTMLSpanElement;
  statusEl: HTMLSpanElement;
  actionRows: HTMLDivElement[];
  /** Owner-at-render — a change flips lock/salvage row presence → full re-render. */
  isOwner: boolean;
}

/** One trade button (Sell or Buy) for a resource — carries the `bld-menu__act`
 *  class so the generic click handler + refresh gating pick it up. */
function tradeBtnHtml(b: Building, opts: MenuOpts, side: "sell" | "buy", key: string): string {
  const p = TRADE_PRICES[key as keyof typeof TRADE_PRICES];
  const act = `${side}:${key}`;
  const cls = `bld-menu__act bld-menu__trade-btn${opts.inRange ? "" : " bld-menu__act--oor"}${tradeRowOff(b, opts, act) ? " bld-menu__act--off" : ""}`;
  const chip = side === "sell" ? `+${p.sell}g` : `-${p.buy}g`;
  const verb = side === "sell" ? "Sell" : "Buy";
  return `<div class="${cls}" data-act="${act}">${verb} <span class="cost">${chip}</span></div>`;
}

export function createBuildingMenu(): BuildingMenu {
  const root = document.createElement("div");
  root.className = "bld-menu";
  root.style.display = "none";
  document.body.appendChild(root);

  let curId: string | null = null;
  let refs: MenuRefs | null = null;
  // Active Storage trade tab — persists across re-renders + reopens within a session.
  let storageTab: "normal" | "rare" = "normal";
  const handlers: Array<(act: { buildingId: string; action: string }) => void> = [];

  function close() {
    curId = null;
    refs = null;
    root.style.display = "none";
    root.innerHTML = "";
  }

  function open(b: Building, opts: MenuOpts) {
    curId = b.id;
    root.style.display = "flex";
    render(b, opts);
  }

  function render(b: Building, opts: MenuOpts) {
    const type = b.buildingType as BuildingType;
    // BUILDING_DEFS lookup is currently unused after the simplification, but we
    // reference it to keep the import meaningful (and to fail loudly if an unknown
    // type ever lands).
    void BUILDING_DEFS[type];
    const stateLabel = b.state === "constructing" ? "building..." : "ready";
    const hdr = `<div class="bld-menu__hdr">${type} <span class="bld-menu__hp">${b.hp}/${b.hpMax}</span> <span class="bld-menu__status">${stateLabel}</span></div>`;

    if (type === "StorageOutpost") {
      // Dedicated tabbed two-button layout: Normal | Rare, one row per resource
      // with a Sell + Buy button. Both tab panels render (inactive hidden) so
      // tab-switching is pure client visibility — no re-render, stable click DOM.
      const ownerRows = opts.isOwner
        ? `<div class="bld-menu__act" data-act="lockStorage">${b.locked ? "Unlock Storage" : "Lock Storage"}</div>
           <div class="bld-menu__act" data-act="salvage">Salvage (50%)</div>`
        : "";
      root.innerHTML = `${hdr}
        <div class="bld-menu__tabs">
          ${TRADE_GROUPS.map((g) => `<div class="bld-menu__tab${g.id === storageTab ? " bld-menu__tab--active" : ""}" data-tab="${g.id}">${g.label}</div>`).join("")}
        </div>
        <div class="bld-menu__body">
          ${TRADE_GROUPS.map((g) => `
            <div class="bld-menu__tabpanel${g.id === storageTab ? "" : " bld-menu__tabpanel--hidden"}" data-panel="${g.id}">
              ${g.resources.map((k) => `<div class="bld-menu__trade-row"><span class="bld-menu__trade-label">${TRADE_PRICES[k].label}</span>${tradeBtnHtml(b, opts, "sell", k)}${tradeBtnHtml(b, opts, "buy", k)}</div>`).join("")}
            </div>`).join("")}
          ${ownerRows}
        </div>
        <div class="bld-menu__close">[Esc] close</div>`;
      // Tab switching — UI-only (toggle active tab + panel visibility), no re-render.
      root.querySelectorAll<HTMLDivElement>(".bld-menu__tab[data-tab]").forEach((tabEl) => {
        tabEl.addEventListener("click", () => {
          const id = tabEl.dataset.tab as "normal" | "rare";
          if (!id || id === storageTab) return;
          storageTab = id;
          root.querySelectorAll<HTMLDivElement>(".bld-menu__tab").forEach((t) => t.classList.toggle("bld-menu__tab--active", t.dataset.tab === id));
          root.querySelectorAll<HTMLDivElement>(".bld-menu__tabpanel").forEach((p) => p.classList.toggle("bld-menu__tabpanel--hidden", p.dataset.panel !== id));
        });
      });
    } else {
      const acts = visibleActions(type, opts.isOwner);
      const rowClass = (a: MenuAction) =>
        `bld-menu__act${opts.inRange ? "" : " bld-menu__act--oor"}${tradeRowOff(b, opts, a.id) ? " bld-menu__act--off" : ""}`;
      root.innerHTML = `${hdr}
        <div class="bld-menu__body">
          ${acts.map((a) => `<div class="${rowClass(a)}" data-act="${a.id}">${actLabel(a, b)}${a.cost ? ` <span class='cost'>(${a.cost})</span>` : ""}</div>`).join("")}
          ${acts.length === 0 ? `<div class="bld-menu__act bld-menu__act--oor">No actions in v1.</div>` : ""}
        </div>
        <div class="bld-menu__close">[Esc] close</div>`;
    }

    const hpEl = root.querySelector<HTMLSpanElement>(".bld-menu__hp")!;
    const statusEl = root.querySelector<HTMLSpanElement>(".bld-menu__status")!;
    const rowEls = Array.from(root.querySelectorAll<HTMLDivElement>(".bld-menu__act[data-act]"));
    refs = { bId: b.id, hpEl, statusEl, actionRows: rowEls, isOwner: opts.isOwner };

    rowEls.forEach((el) => {
      el.addEventListener("click", () => {
        const act = el.dataset.act;
        if (!act) return;
        // Re-read the latest live oor/off flags off the DOM rather than capturing
        // `opts` at render time — refresh() updates the classes, so this gives
        // correct gating without rebuilding listeners.
        if (act === "close") return;
        if (el.classList.contains("bld-menu__act--oor")) return;
        if (el.classList.contains("bld-menu__act--off")) return;
        if (curId) for (const h of handlers) h({ buildingId: curId, action: act });
      });
    });
  }

  function refresh(b: Building, opts: MenuOpts) {
    if (curId !== b.id) return;
    // Building swap (rare but possible) — full re-render to rebuild the action list.
    if (!refs || refs.bId !== b.id) {
      open(b, opts);
      return;
    }
    // Owner change flips lock/salvage row presence (both layouts) — full re-render.
    // (Exercised when an owner disconnects mid-menu; not the v1 happy path but
    // cheap to keep correct.)
    if (opts.isOwner !== refs.isOwner) {
      open(b, opts);
      return;
    }
    // Diff: hp text, state text, oor class, trade-off class, lock label. Stable
    // DOM, no listener churn.
    const hpText = `${b.hp}/${b.hpMax}`;
    if (refs.hpEl.textContent !== hpText) refs.hpEl.textContent = hpText;
    const stateLabel = b.state === "constructing" ? "building..." : "ready";
    if (refs.statusEl.textContent !== stateLabel) refs.statusEl.textContent = stateLabel;
    for (const row of refs.actionRows) {
      const act = row.dataset.act ?? "";
      row.classList.toggle("bld-menu__act--oor", !opts.inRange);
      row.classList.toggle("bld-menu__act--off", tradeRowOff(b, opts, act));
      if (act === "lockStorage") {
        const want = b.locked ? "Unlock Storage" : "Lock Storage";
        if (row.firstChild && row.firstChild.textContent !== want) row.firstChild.textContent = want;
      }
    }
  }

  function isOpen() { return curId !== null; }
  function currentId() { return curId; }
  function onAction(h: (a: { buildingId: string; action: string }) => void) { handlers.push(h); }
  return { open, close, refresh, isOpen, currentId, onAction };
}
